import vuetify from "@/plugins/vuetify";
import moment from "moment-timezone";
import store from "@/store/index";
import Storage from "./storage";
import * as lodash from "lodash";
import _ from "lodash";
import { StorageConstants } from "@/enums/storage.enum";
import { AppLang, Langs } from "@/types/lang";
import { AppsConstraints } from "@/enums/apps.enum";

/**
 * Globalise Moment Js
 */
window.moment = moment || null;

/**
 * Globalise Lodash
 */
window._ = lodash || {};

export const Lang = {
  isRTL: false,

  /**
   * Get application language
   * @return {Lang} current lang
   */
  get(): Langs {
    return Storage.get(StorageConstants.LANG) || AppLang.EN;
  },

  /**
   * Set application language
   * @return {void}
   */
  set(lang: string = AppLang.EN): void {
    Storage.set(StorageConstants.LANG, lang, false);
    store.commit("App/setAppLang", lang);
    this.isRTL = AppLang.AR === lang;
    vuetify.framework.lang.current = lang;
    vuetify.framework.rtl = this.isRTL;
  },

  /**
   * Prepare & Init application language
   * @return {void}
   */
  prepare(): void {
    const lang = this.get() || AppLang.EN;
    this.set(lang);
    // store.commit("App/setAppLang", lang);
  },

  /**
   * Switch application language
   * @return {void}
   */
  switch(): void {
    const current: Langs = this.get();
    switch (current) {
      case AppLang.AR:
        return this.set(AppLang.EN);

      case AppLang.EN:
      default:
        return this.set(AppLang.AR);
    }
  },
};

export const Helper = {
  /**
   * Scroll to element
   * @param {string} select element that wont to be move to
   */
  scrollTo(select: string) {
    const selector: any = document.getElementById(select);
    selector.scrollIntoView();
  },

  /**
   * Clone array
   * @param   {Array} original the original array
   * @return {Array} cloned new array with original data
   */
  cloneArray(original) {
    return _.clone(JSON.stringify(original));
  },

  /**
   * Deep diff between two object-likes
   * @param  {Object} fromObject the original object
   * @param  {Object} toObject   the updated object
   * @return {Object}            changes size
   */
  deepDiff(fromObject: Object, toObject: Object) {
    const changes: Object = {};

    const buildPath = (path, obj, key) => (_.isUndefined(path) ? key : `${path}.${key}`);

    const walk = (fromObject, toObject, path = "") => {
      for (const key of _.keys(fromObject)) {
        const currentPath = buildPath(path, fromObject, key);
        if (!_.has(toObject, key)) {
          changes[currentPath] = { from: _.get(fromObject, key) };
        }
      }

      for (const [key, to] of _.entries(toObject)) {
        const currentPath = buildPath(path, toObject, key);
        if (!_.has(fromObject, key)) {
          changes[currentPath] = { to };
        } else {
          const from = _.get(fromObject, key);
          if (!_.isEqual(from, to)) {
            if (_.isObjectLike(to) && _.isObjectLike(from)) {
              walk(from, to, currentPath);
            } else {
              changes[currentPath] = { from, to };
            }
          }
        }
      }
    };

    walk(fromObject, toObject);

    return _(changes).size();
  },

  /**
   * Object size
   * @param  {Object}  object the original object
   * @return {number} object size
   */
  objectSize(object = {}) {
    return _(object).size();
  },

  /**
   * Is Object empty
   * @param  {Object} object the original object
   * @return {number} object size
   */
  isObjectEmpty(object: Object = {}): number {
    return _.isEmpty(object);
  },

  /**
   * Create UUID
   * @return {string} uuid
   */
  createUUID(): string {
    // ^ NOR Diff of 2 items
    // & AND True/False if EVEN/EVEN
    // >> Shift right
    // [1e7]+-1e3+-4e3+-8e3+-1e11 => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'

    // @ts-ignore
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
      (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
    );
  },

  normalizeArabicText(text: string = ""): string {
    //remove special characters
    text = text?.replace(/([^\u0621-\u063A\u0641-\u064A\u0660-\u0669a-zA-Z 0-9])/g, "");

    //normalize Arabic
    text = text?.replace(/(آ|إ|أ)/g, "ا");
    text = text?.replace(/(ة)/g, "ه");
    text = text?.replace(/(ئ|ؤ)/g, "ء");
    text = text?.replace(/(ى)/g, "ي");
    text = text?.replace(/(منطقه)/g, "");
    text = text?.replace(/(منطقة)/g, "");
    text = text?.replace(/(محافظة)/g, "");
    text = text?.replace(/(محافظه)/g, "");

    //convert arabic numerals to english counterparts.
    const starter = 0x660;
    for (let i = 0; i < 10; i++) {
      text?.replace(String.fromCharCode(starter + i), String.fromCharCode(48 + i));
    }

    return text?.toString()?.trim();
  },

  /**
   * Format price
   * @return {string} price
   */
  formatPrice(price) {
    return (parseInt(price) / 1000).toFixed();
  },

  /**
   * Move customer Object
   * @return <CustomerInterface>
   */
  moveCustomerObject(oldCustomer, newCustomer, isCreate = false) {
    // update address
    const sAddress = oldCustomer?.addresses?.find((OAddress) => !!OAddress.selected);
    if (sAddress) {
      newCustomer?.addresses?.map((address) => {
        address.selected = address.id === sAddress.id;
        return address;
      });
    }

    // update phone number
    const sPhone = oldCustomer?.phone_numbers?.find((OPhone) => !!OPhone.selected);
    if (sPhone) {
      newCustomer?.phone_numbers?.map((phone) => {
        phone.selected = sPhone.id === phone.id;
        return phone;
      });
    }

    return newCustomer;
  },

  /**
   * Get Browser Details
   * @return {string}
   */
  getBrowserDetails(): string {
    const { appName, appCodeName, appVersion, vendor, language, userAgent, onLine } = navigator;

    return JSON.stringify({
      appName,
      appCodeName,
      appVersion,
      vendor,
      language,
      userAgent,
      onLine,
    });
  },

  /**
   * Convert 1 digit number to 2 digits
   * @return {string}
   */
  to2Digit(n: number): string {
    return n > 9 ? `${n}` : `0${n}`;
  },

  /**
   * Convert 1 digit number to 2 digits
   * @return {string}
   */
  getURIAppName(): string {
    return window.location.href?.replace(`${window.location.origin}/`, "")?.split("/")?.[0]?.toUpperCase();
  },

  /**
   * Get User is Call Center
   * @return {boolean}
   */
  isUserAllowedCallCenter() {
    const isAgent = Storage.get(StorageConstants.SESSION)?.is_agent || false;
    let isCallCenterApp =
      Storage.get(StorageConstants.BUSINESS)?.services?.findIndex((item) => item.name === AppsConstraints.CALL_CENTER) >
      -1;

    return isAgent && isCallCenterApp;
  },

  /**
   * Handle on set google tag manager
   * @return {void}
   */
  setGoogleTagManager(): void {
    ((i, s, o, g, r, a, m) => {
      i["GoogleTagManager"] = r;
      (i[r] =
        i[r] ||
        function () {
          (i[r].q = i[r].q || []).push(arguments);
        }),
        (i[r].l = 1 * new Date().getTime());
      (a = <any>s.createElement(o)), (m = <any>s.getElementsByTagName(o)[0]);
      (<any>a).async = 1;
      (<any>a).src = g;
      (<any>m).parentNode.insertBefore(a, m);
    })(window, document, "script", process.env.VUE_APP_GTM_API_PATH, "ga");

    (<any>window).dataLayer = (<any>window).dataLayer || [];

    function gtag(key, value) {
      (<any>window).dataLayer.push({ key, value });
    }

    gtag("js", new Date());
    gtag("config", process.env.VUE_APP_GTM_CONFIG_KEY);
  },

  /**
   * Search on String
   * @param string
   * @param search
   * @return {boolean}
   */
  searchOnString(string, search) {
    return string.search(search) !== -1;
  },

  /**
   * Convert time from 12 to 24
   * @param time
   * @return {String}
   */
  convert12to24(time: any) {
    if (!time || !time?.A) {
      return "";
    }

    if (time.A !== "PM" && time.A !== "P") {
      return time.hh + ":" + time.mm;
    }

    const hour = parseInt(time.hh) + 12;

    if (hour < 24 && !!hour) {
      return `${hour}:${time.mm}`;
    }

    return `00:${time.mm}`;
  },

  /**
   * Convert time from 12 to 24
   * @param value
   * @return {String}
   */
  convertString12to24(value: any) {
    const time = value.split(":");
    const hh = this.convert24hTo12h(parseInt(time[0])) || 12;
    const mm = time[1];
    return `${hh}:${mm}:`;
  },

  convert24hTo12h(hour) {
    if (!hour) {
      hour = 12;
    }
    if (hour > 12) {
      hour -= 12;
    }

    return Helper.to2Digit(hour);
  },

  /**
   * Comma Separated
   * @param value
   * @return {any}
   */
  formatNumberWithComma(value: number): any {
    // return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parseFloat(`${value}`).toLocaleString();
  },

  /**
   * find difference between 2 object
   * @param object
   * @param base
   */
  difference(object, base): any {
    return _.transform(object, (result, value, key) => {
      if (!_.isEqual(value, base?.[key])) {
        result[key] = _.isObject(value) && _.isObject(base?.[key]) ? this.difference(value, base?.[key]) : value;
      }
    });
  },

  /**
   * is there a difference between 2 object
   * @param object
   * @param base
   */
  isDifference(object, base): any {
    return !_.isEmpty(this.difference(object, base));
  },
};
